import React from 'react';
import { Box, BoxProps } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 *
 * IconRow and IconRowCell help maintain a uniform spacing and alignment
 * among instances of an icon adjacent to text or an input.
 *
 * Intended use:
 * <IconRow>
 *   <IconRowCell type="icon">
 *     ... an icon ...
 *   </IconRowCell>
 *   <IconRowCell>
 *     ... content ...
 *   </IconRowCell>
 * </IconRowCell>
 *
 * There are three types of cells:
 *   -- icon — optional; fixed width
 *   -- text - required; will stretch to fill available space
 *   -- actions - optional; for buttons
 *
 * There should be no more than one of each within a row.
 *
 * Possible parameters to pass to IconRow
 *   -- isHeader - Gives extra bottom margin
 *   -- isInput - Improves the alignment of the icon
 *                in relation to common Material input components
 *   -- noIcon - Allows the text cell to stretch all the way to the
 *               left margin. if noIcon is false but there is no "icon"
 *               cell, then there will still be empty space where the "icon"
 *               cell would normally be, allowing the "text" cell
 *               to align with other IconRows that might be present.
 *   -- size="small" - Decreases the space allotted to the icon cell
 *
 * IconRow and IconRowCell props both extend Material's Box properties,
 * meaning they can accept any of the properties that a Box can, such as
 * className, style, xs, and alignSelf. This is useful for when an edge case
 * requires the IconRow behavior to be overridden.
 *
 */

const normalColumns = `
  [start icon-start]
  2.5rem
  [icon-end text-start]
  1fr
  [text-end actions-start]
  auto
  [actions-end end]`;

const smallColumns = `
  [start icon-start]
  1.5rem
  [icon-end text-start]
  1fr
  [text-end actions-start]
  auto
  [actions-end end]`;

const noIconColumns = `
  [start text-start]
  1fr
  [text-end actions-start]
  auto [actions-end end]`;

interface StyledProps {
  isHeader?: boolean;
  isInput?: boolean;
  noIcon?: boolean;
  size?: 'small' | 'normal';
}

const StyledIconRow = styled(Box, {
  shouldForwardProp: (prop) => !['isHeader', 'isInput', 'noIcon', 'size'].includes(prop as string),
})<StyledProps>(({ isHeader, isInput, noIcon, size }) => ({
  alignItems: 'flex-start',
  display: 'grid',
  gridGap: size === 'small' ? '0.5rem' : '1rem',
  gridTemplateColumns: noIcon ? noIconColumns : size === 'small' ? smallColumns : normalColumns,
  marginBottom: isHeader ? '1rem' : '0.5rem',
  ...(isHeader && { '& > *': { alignSelf: 'center' } }),
  ...(isInput && {
    alignItems: 'baseline',
    '& > :first-of-type': {
      alignSelf: 'baseline',
      transform: 'translateY(0.5rem)',
    },
    '& > :nth-of-type(2)': {
      alignSelf: 'start',
    },
  }),
}));

interface Props extends BoxProps {
  isHeader?: boolean;
  isInput?: boolean;
  noIcon?: boolean;
  size?: 'small' | 'normal';
}

const IconRow: React.FC<Props> = (props: Props) => <StyledIconRow {...(props as any)} />;

export default IconRow;
